import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSubscription,
  createCheckoutSession,
  createPortalSession,
  startTrial,
  getBillingSummary,
  getUpcomingInvoice,
} from "../api/billing.js";

export function useSubscription(orgId: string | undefined) {
  return useQuery({
    queryKey: ["subscription", orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error("Organization ID is required");
      }
      return getSubscription(orgId);
    },
    enabled: orgId != null,
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: ({
      orgId,
      planId,
      websiteId,
    }: {
      orgId: string;
      planId: string;
      websiteId: string;
    }) => createCheckoutSession(orgId, planId, websiteId),
  });
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: ({ orgId }: { orgId: string }) => createPortalSession(orgId),
  });
}

export function useStartTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      planId,
      websiteId,
    }: {
      orgId: string;
      planId: string;
      websiteId: string;
    }) => startTrial(orgId, planId, websiteId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ["wizard", variables.websiteId] });
      void qc.invalidateQueries({
        queryKey: ["subscription", variables.orgId],
      });
    },
  });
}

export function useBillingSummary(orgId: string | undefined) {
  return useQuery({
    queryKey: ["billing-summary", orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error("Organization ID is required");
      }
      return getBillingSummary(orgId);
    },
    enabled: orgId != null,
  });
}

export function useUpcomingInvoice(orgId: string | undefined) {
  return useQuery({
    queryKey: ["upcoming-invoice", orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error("Organization ID is required");
      }
      return getUpcomingInvoice(orgId);
    },
    enabled: orgId != null,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
